import React, { ChangeEvent, forwardRef, useCallback, useImperativeHandle, useRef, useState } from  "react";
import { RCCGrid, RCCHookToast, RCCIcon, RCCInput, RCCInputMask, RCCRefModalProps, RCCServicesApi, RCCSwitch } from "react-common-components";
import { useForm } from "react-hook-form";
import Audit from "src/components/Audit";
import CardContractItemType from "src/components/CardContractItemType";
import DeleteModal from "src/components/DeleteModal";
import Modal from "src/components/Modal";
import ModalSide from "src/components/Modal/styles";
import Select from "src/components/Select";
import { useAuth } from "src/context/Auth/context";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import { IComponentBusinessModals, IEftba002, IRefBusinessModals } from "./interface";
import { addAndChangeSchema, deleteSchema } from "./schemas";
import DivProfilePic from "./styles";

const Modals: React.ForwardRefRenderFunction<IRefBusinessModals, IComponentBusinessModals> = ({
	paginationRef
}, ref) => {

	const { translate } = useTranslation();
	const {addToast} = RCCHookToast();
	const { updateCompanyImage, updateCompanyName, company } = useAuth();
	const { setLoading } = useLoaderBackdrop();

	const modalDelete = useRef<RCCRefModalProps>(null);

	const { 
		handleSubmit, 
		register, 
		control, 
		reset, 
		setError, 
		formState: { errors }, 
		clearErrors
	} = useForm();

	const { 
		handleSubmit : handleSubmitDelete, 
		register : registerDelete, 
		reset : resetDelete, 
		setError : setErrorDelete, 
		formState: { errors : errorsDelete }, 
		clearErrors: clearErrorsDelete 
	} = useForm();

	const [isAdd, setIsAdd] = useState(false);
	const [selectedBussiness, setSelectedBussiness] = useState<IEftba002>({} as IEftba002);
	const modalAddOrChange = useRef<RCCRefModalProps>(null);
	const businessGroupId = useRef<number | undefined>(undefined);
	const { REACT_APP_S3_ADDRESS, REACT_APP_S3_DIRECTORY } = process.env;
	const onCallbackAddOrChange = useRef<Function | undefined>(undefined);

	const queryData = async (id: number) => {
		setLoading(true);
		try{
			const { data } = await RCCServicesApi.getRequest({
				method: "Business/Select",
				urlParameters : {
					id
				}
			});
			setSelectedBussiness(data);
			setLoading(false);
			modalAddOrChange.current?.openModal();
		}catch(err: any){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao buscar dados"), addToast);
		}
	};

	const submitAddOrChange = async (data: any) => {
		try{
			clearErrors();
			modalAddOrChange.current?.setLoadingButton();
			
			await addAndChangeSchema(translate).validate({...data}, {
				abortEarly: false
			});

			if(isAdd){
				await RCCServicesApi.postRequest({
					method: "Business/Insert",
					body: {
						...data,
						efa002Efa001Id: businessGroupId.current ?? null,
						efa002Cnpj: data.efa002Cnpj.replace(/\D/g, ""),
						efa002Ativo: data.efa002Ativo ? "S" : "N"
					}
				});
			}else{
				await RCCServicesApi.putRequest({
					method: "Business/Edit",
					body: {
						...data,
						efa002Id: selectedBussiness.efa002Id,
						efa002Cnpj: data.efa002Cnpj.replace(/\D/g, ""),
						efa002Ativo: data.efa002Ativo ? "S" : "N",
						prevEfa002Ativo: selectedBussiness.efa002Ativo
					}
				});

				if(company.efa002Id === selectedBussiness.efa002Id)
					updateCompanyName(data.efa002Fantasia);
			}

			addToast({
				color: "green",
				description: isAdd ? translate("Empresa adicionada com sucesso") : translate("Empresa alterada com sucesso"),
				title: translate("Sucesso")
			});

			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();
			paginationRef.current?.reload();

			if(onCallbackAddOrChange.current)
				onCallbackAddOrChange.current();
		}catch(err: any){

			modalAddOrChange.current?.resetLoadingButton();

			if(getValidationErrors(err, setError))
				return;

			const textMessageError = err?.response?.data?.Message;
			if(textMessageError === "cnpjIsAlreadyInUse"){
				addToast({
					title: translate("Erro"),
					description: translate("Este CNPJ já está cadastrado"),
					color: "red"
				});
			}else if(textMessageError === "invalidCnpj"){
				addToast({
					title: translate("Erro"),
					description: translate("Este CNPJ é inválido"),
					color: "red"
				});
			}else{
				standardErrorMessages(err, isAdd ? translate("Erro ao adicionar empresa") : translate("Erro ao alterar empresa"), addToast);
			}

		}
	};

	const submitDelete = async (data: any) => {
		try{	
			clearErrorsDelete();
			modalDelete.current?.setLoadingButton();

			await deleteSchema(translate).validate({...data, "efa002FantasiaConfirmation": selectedBussiness.efa002Fantasia},{
				abortEarly: false
			});

			await RCCServicesApi.deleteRequest({
				method: "Business/Delete",
				urlParameters: {
					id: selectedBussiness.efa002Id
				}
			});

			addToast({
				color: "green",
				description: translate("Empresa excluída com sucesso"),
				title: translate("Sucesso")
			});
			modalDelete.current?.resetLoadingButton();
			modalDelete.current?.closeModal();
			paginationRef.current?.reload();

		}catch(err: any){

			modalDelete.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorDelete))
				return;

			const textMessageError = err?.response?.data?.Message;
			if(textMessageError === "noPermission"){
				addToast({
					title: translate("Erro"),
					description: translate("Você não possui permissão para excluir esta empresa"),
					color: "red"
				});
			}else{
				standardErrorMessages(err, translate("Erro ao excluir empresa"), addToast);
			}
		}	
	};

	const changeProfilePic = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
		if(!e.target.files)
			return;
			
		if(!["image/jpg", "image/jpeg", "image/png"].includes(e.target.files[0].type)){
			addToast({
				color: "red",
				title: "Erro",
				description: "Você só pode fazer uploads de arquivos jpg, jpeg ou png"
			});
			return;
		}
		
		if(e.target.files[0].size > 150000){
			addToast({
				color: "red",
				title: "Erro",
				description: "Você não pode fazer upload de imagens com tamanho maior que 150 kB"
			});
			return;
		}


		try{
			const formData = new FormData();
			formData.append("Files", e.target.files[0]);
			const  { data } = await RCCServicesApi.postRequest({
				method: "Business/ChangeBusinessImage",
				body: formData,
				urlParameters: {
					efa002Id: selectedBussiness.efa002Id
				},
				contentType: "multipart/form-data"
			});

			if(company.efa002Id === selectedBussiness.efa002Id){
				updateCompanyImage(data.efa002Imagem);
			}else{
				setSelectedBussiness((prevState) => ({
					...prevState,
					efa002Imagem: data.efa002Imagem
				}));
			}
			

			addToast({
				color: "green",
				title: translate("Sucesso"),
				description: translate("Imagem da empresa atualizada com sucesso")
			});
		}catch(err){
			standardErrorMessages(err,  "Erro ao trocar imagem da empresa" , addToast);
		}		
	}, [selectedBussiness]);

	const actionButtonAdd = (efa001Id?: number, onCallback?: Function, efa001Razao?: string, efa001Fantasia?: string) => {
		reset();
		setIsAdd(true);
		businessGroupId.current = efa001Id;
		setSelectedBussiness({
			efa002Razao: efa001Razao,
			efa002Fantasia: efa001Fantasia
		} as IEftba002);
		onCallbackAddOrChange.current = onCallback;
		modalAddOrChange.current?.openModal();
	};

	const actionButtonChange = (row: IEftba002) => {
		reset();
		setIsAdd(false);
		onCallbackAddOrChange.current = undefined;
		queryData(row.efa002Id);
	};

	const actionButtonDelete = (row: IEftba002) => {
		resetDelete(); 
		setSelectedBussiness(row); 
		modalDelete.current?.openModal();
	};

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange,
		actionButtonDelete
	}));
    

	return (
		<>
			<DeleteModal
				ref={modalDelete}
				title= {translate("Exclusão de empresa")}
				description= {`${translate("Deseja realmente excluir a empresa")}: ${selectedBussiness.efa002Fantasia}?`}
				onSubmit = {handleSubmitDelete(submitDelete)} 
				height="250px"
			>
				<RCCGrid
					container
					item
					xs={10}
				>
					<RCCInput 
						register={registerDelete}
						name="efa002Fantasia"
						label={translate("Digite o nome da empresa para excluí-la")}
						maxLength={200}
						error={errorsDelete.efa002Fantasia}
						required
					/>
				</RCCGrid>
			</DeleteModal>
			<Modal
				ref={modalAddOrChange}
				header={{
					title: isAdd ? translate("Adicionar Empresa") : translate("Alterar Empresa"),
					subtitle: !isAdd ? `${translate("Empresa")}: ${selectedBussiness.efa002Fantasia}`: "",
					icon: {
						name: "building"
					}
				}}
				onSubmit={handleSubmit(submitAddOrChange)}
				styleInline={ModalSide}
			>
				<RCCGrid
					container
					item
					direction="column"
					alignItems="center"
				>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
						alignItems="center"
						justify="flex-end"
					>
						<Audit data={selectedBussiness} type="pop"/>
					</RCCGrid>
					{!isAdd && (
						<>
							<RCCGrid container item 
								xs={12}
								sm={10}
								alignItems="center"
								justify="flex-end"
								style={{marginTop: "0.5rem"}}
							>
								<CardContractItemType 
									type="18"
								/>
							</RCCGrid>
							<RCCGrid
								container
								item
								xs={12}
								sm={10}
							>
								<DivProfilePic>

									<div className="company-img-div">
										{selectedBussiness.efa002Imagem ? (
											<>
												<img className="company-img" src={`${REACT_APP_S3_ADDRESS}/${REACT_APP_S3_DIRECTORY}/imagens_empresas/${company.efa002Id === selectedBussiness.efa002Id ? company.efa002Imagem : selectedBussiness.efa002Imagem}`} alt="Company Logo"/>
											</>	
										) : (
											<div className="company-placeholder">
												<p>{translate("Imagem da empresa")}</p>
											</div>
										)}
									</div>

									<label htmlFor="business-pic">
										<RCCIcon name="camera" />
										<input 
											id="business-pic" 
											type="file" 
											onChange={changeProfilePic}
										/>
									</label>
								</DivProfilePic>
							</RCCGrid>
						</>
					)}

					<RCCGrid
						container
						item
						xs={12}
						sm={10}
					>
						<RCCInput 
							register={register}
							name="efa002Razao"
							label={translate("Razão Social")}
							maxLength={200}
							defaultValue={selectedBussiness.efa002Razao ?? ""}
							error={errors.efa002Razao}
							required
						/>
					</RCCGrid>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
					>
						<RCCInput 
							register={register}
							name="efa002Fantasia"
							label={translate("Nome Fantasia")}
							maxLength={200}
							defaultValue={selectedBussiness.efa002Fantasia ?? ""}
							error={errors.efa002Fantasia}
							required
						/>
					</RCCGrid>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
					>
						<RCCInputMask 
							control={control}
							name="efa002Cnpj"
							label={translate("CNPJ")}
							defaultValue={selectedBussiness.efa002Cnpj ?? ""}
							error={errors.efa002Cnpj}
							required
							format="##.###.###/####-##"/>
					</RCCGrid>

					<RCCGrid
						container
						item
						xs={12}
						sm={10}
					>
						<Select 
							control={control}
							name="efa002Uf" 
							options={[
								{value: "AC", label: "Acre"}, 
								{value: "AL", label: "Alagoas"},
								{value: "AP", label: "Amapá"},
								{value: "AM", label: "Amazonas"},
								{value: "BA", label: "Bahia"},
								{value: "CE", label: "Ceará"},
								{value: "DF", label: "Distrito Federal"},
								{value: "ES", label: "Espírito Santo"},
								{value: "GO", label: "Goiás"},
								{value: "MA", label: "Maranhão"},
								{value: "MT", label: "Mato Grosso"},
								{value: "MS", label: "Mato Grosso do Sul"},
								{value: "MG", label: "Minas Gerais"},
								{value: "PA", label: "Pará"},
								{value: "PB", label: "Paraíba"},
								{value: "PR", label: "Paraná"},
								{value: "PE", label: "Pernambuco"},
								{value: "PI", label: "Piauí"},
								{value: "RJ", label: "Rio de Janeiro"},
								{value: "RN", label: "Rio Grande do Norte"},
								{value: "RS", label: "Rio Grande do Sul"},
								{value: "RO", label: "Rondônia"},
								{value: "RR", label: "Roraima"},
								{value: "SC", label: "Santa Catarina"},
								{value: "SP", label: "São Paulo"},
								{value: "SE", label: "Sergipe"},
								{value: "TO", label: "Tocantins"}	
							]} 
							label={translate("UF")}
							defaultValue={selectedBussiness.efa002Uf}
							submit="value"
							error={errors.efa002Uf}
							required
						/>	
					</RCCGrid>

					<RCCGrid 
						item 
						container
						xs={12}
						sm={10}
						style={{marginTop: "10px"}}
					>
						<RCCSwitch 
							label={translate("Ativo")} 
							name="efa002Ativo" 
							defaultChecked={selectedBussiness.efa002Ativo === "S" || isAdd} 
							error={errors.efa002Ativo}
							register={register}/>
					</RCCGrid>
				</RCCGrid>
			</Modal>
		</>
	);
};


export default forwardRef(Modals);
